import { type ReactNode, useState } from "react";
import { useLockDate } from "@api/lock.api";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { Loader } from "@design-system/Loader/Loader";
import { VStack } from "@design-system/VStack/VStack";
import { usePortalContainer } from "@design-system/overlay/OverlayContainerContext";
import { getFirstUnlockedDateOnly, laterDateOnly } from "@helpers/lock";
import { AccountsIcon, FolderIcon, PlayIcon } from "@icons";
import { ScheduleFormSection } from "../ScheduleFormSection";
import { ScheduleManualAccountsSection } from "./ScheduleManualAccountsSection";
import { ScheduleManualRecognitionSection } from "./ScheduleManualRecognitionSection";
import {
    ScheduleManualSection,
    ScheduleManualStaticSection,
} from "./ScheduleManualSection";
import { MigratedScheduleCheckbox } from "./MigratedScheduleCheckbox";
import { ScheduleDraft } from "./scheduleDraft";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import {
    MIGRATED_SCHEDULE_ROW_LABELS,
    SCHEDULE_ROW_LABELS,
} from "./scheduleRowLabels";
import { MigratedScheduleOption } from "./useMigratedScheduleOption";
import drawerStyles from "../ScheduleDrawer.module.scss";

const ALL_SECTIONS = ["accounts", "recognition"];

/** Decided once when the draft first loads, so picking an account never closes the section being filled in. */
function getDefaultOpenSections(draft: ScheduleDraft): string[] {
    return draft.holdingAccountId && draft.recognitionAccountId
        ? ["recognition"]
        : ALL_SECTIONS;
}

interface ScheduleManualBodyProps {
    template: ScheduleTemplateResponse | undefined;
    entityId: number | null;
    draft: ScheduleDraft | null;
    onDraftChange: (draft: ScheduleDraft) => void;
    draftError: string | null;
    /** False once the schedule can no longer change. */
    isEditable: boolean;
    areRowsLocked?: boolean;
    isSpecificDateEditable: boolean;
    isHoldingAccountEditable: boolean;
    layout: ScheduleManualLayout;
    /** Fields above the sections, such as the entity and source label on create. */
    details?: ReactNode;
    migratedOption?: MigratedScheduleOption | null;
}

export const ScheduleManualBody = ({
    template,
    entityId,
    draft,
    onDraftChange,
    draftError,
    isEditable,
    areRowsLocked,
    isSpecificDateEditable,
    isHoldingAccountEditable,
    layout,
    details,
    migratedOption,
}: ScheduleManualBodyProps) => {
    const portalContainer = usePortalContainer();
    const lockMinDate = getFirstUnlockedDateOnly(useLockDate(entityId));
    const [openSections, setOpenSections] = useState<string[] | null>(null);

    if (draft && openSections === null) {
        setOpenSections(getDefaultOpenSections(draft));
    }

    if (!template || !draft) {
        return <Loader />;
    }
    const visibleSections = openSections ?? ALL_SECTIONS;
    const minDate = laterDateOnly(lockMinDate, migratedOption?.minDate);
    const migratedCheckbox = migratedOption && (
        <MigratedScheduleCheckbox
            option={{
                ...migratedOption,
                onCheckedChange: (isChecked) => {
                    if (isChecked) {
                        setOpenSections(ALL_SECTIONS);
                    }
                    migratedOption.onCheckedChange(isChecked);
                },
            }}
        />
    );
    const accounts = (
        <ScheduleManualAccountsSection
            template={template}
            entityId={entityId}
            draft={draft}
            onDraftChange={onDraftChange}
            isEditable={isEditable}
            isHoldingAccountEditable={isEditable && isHoldingAccountEditable}
            layout={layout}
            portalContainer={portalContainer}
        />
    );
    const recognition = (
        <ScheduleManualRecognitionSection
            draft={draft}
            onDraftChange={onDraftChange}
            isEditable={isEditable}
            areRowsLocked={areRowsLocked}
            isSpecificDateEditable={isSpecificDateEditable}
            errorMessage={draftError}
            minDate={minDate}
            portalContainer={portalContainer}
            labels={
                migratedOption?.isChecked
                    ? MIGRATED_SCHEDULE_ROW_LABELS
                    : SCHEDULE_ROW_LABELS
            }
            layout={layout}
        />
    );

    if (layout === "drawer") {
        return (
            <div>
                {details && (
                    <ScheduleFormSection
                        title="Schedule details"
                        icon={<FolderIcon className="icon-color-gray-600" />}
                        iconClassName={drawerStyles.sectionIconDetails}
                    >
                        {details}
                    </ScheduleFormSection>
                )}
                <ScheduleFormSection
                    title="Accounts"
                    icon={<AccountsIcon className="icon-color-gray-700" />}
                    iconClassName={drawerStyles.sectionIconAccounts}
                    sectionClassName={drawerStyles.sectionToRecognition}
                >
                    {accounts}
                </ScheduleFormSection>
                <ScheduleFormSection
                    title="Recognition"
                    icon={<PlayIcon className="icon-color-blue-600" />}
                    iconClassName={drawerStyles.sectionIconRecognition}
                    sectionClassName={drawerStyles.sectionLast}
                    headerAddon={migratedCheckbox}
                >
                    {recognition}
                </ScheduleFormSection>
            </div>
        );
    }

    return (
        <VStack gap={16}>
            {details && (
                <ScheduleManualStaticSection label="Details">
                    {details}
                </ScheduleManualStaticSection>
            )}
            <AccordionRoot
                multiple
                value={visibleSections}
                onValueChange={setOpenSections}
            >
                <VStack gap={16}>
                    <ScheduleManualSection value="accounts" label="Accounts">
                        {accounts}
                    </ScheduleManualSection>
                    <ScheduleManualSection
                        value="recognition"
                        label="Recognition"
                        headerActions={migratedCheckbox}
                    >
                        {recognition}
                    </ScheduleManualSection>
                </VStack>
            </AccordionRoot>
        </VStack>
    );
};
